
export function base64ToBlob(base64: string, contentType: string): Blob {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: contentType });
}
import { MatTableDataSource } from '@angular/material/table';

export function isTruncated(text: any, limit: number = 100): boolean {
  if (typeof text !== 'string') text = String(text ?? '');
  return text.length > limit;
}

export function getTruncated(text: any, limit: number = 100): string {
  let safeText = '';
  if (typeof text === 'string') {
    safeText = text;
  } else if (text === undefined || text === null) {
    safeText = '';
  } else {
    try {
      safeText = String(text);
    } catch {
      safeText = '';
    }
  }
  if (safeText.length > limit) {
    return safeText.slice(0, limit) + '';
  }
  return safeText;
}

export function getObjectKeys(obj: any): string[] {
  return obj ? Object.keys(obj) : [];
}

export function groupDataBySection(data: any[]): {
  groupedBySection: { [section: string]: any[] },
  sectionOrder: string[],
  sectionDataSources: { [section: string]: MatTableDataSource<any> }
} {
  const groupedBySection: { [section: string]: any[] } = {};
  const sectionOrder: string[] = [];
  const sectionDataSources: { [section: string]: MatTableDataSource<any> } = {};
  const sectionMeta: {[key: string]: {section: string, order: number|null, items: any[], isMisc: boolean}[]} = {};
  data.forEach(item => {
    const tab = item.tab_name || 'Other';
    const raw = item.section_name || item.section || '';
    item.original_section_name = raw || 'Miscellaneous';
    let sections = raw ? raw.split(',').map((s: string) => s.trim()).filter((s: string) => !!s) : [''];
    if (sections.length === 0) sections = [''];
    sections.forEach((sec: string) => {
      let norm = sec ? sec.replace(/;/g, '').replace(/\s{2,}/g, ' ').trim() : '';
      let isMisc = !norm;
      let sectionKey = isMisc ? 'Miscellaneous' : norm;
      const order = (item.section_order !== undefined && item.section_order !== null && item.section_order !== '') ? Number(item.section_order) : null;
      if (!sectionMeta[tab]) sectionMeta[tab] = [];
      let found = sectionMeta[tab].find(s => s.section === sectionKey);
      if (!found) {
        sectionMeta[tab].push({ section: sectionKey, order, items: [item], isMisc });
      } else {
        found.items.push(item);
        if (order !== null) {
          if (found.order === null) {
            found.order = order;
          } else {
            found.order = Math.min(found.order, order);
          }
        }
      }
    });
  });
  Object.keys(sectionMeta).sort().forEach(tab => {
    let sortedSections = sectionMeta[tab].slice().sort((a, b) => {
      if (a.isMisc && !b.isMisc) return 1;
      if (!a.isMisc && b.isMisc) return -1;
      if (a.isMisc && b.isMisc) {
        const aMin = Math.min(...a.items.map(i => i._rowKey ?? 0));
        const bMin = Math.min(...b.items.map(i => i._rowKey ?? 0));
        return aMin - bMin;
      }
      if (a.order === null && b.order !== null) return 1;
      if (a.order !== null && b.order === null) return -1;
      if (a.order !== null && b.order !== null) {
        if (a.order !== b.order) return a.order - b.order;
      }
      if (a.section && b.section) return a.section.localeCompare(b.section);
      return 0;
    });
    sortedSections.forEach(secObj => {
      let key = secObj.section || 'Miscellaneous';
      const sortedItems = secObj.items.slice().sort((a, b) => {
        const aOrder = a.data_element_order;
        const bOrder = b.data_element_order;
        if (aOrder == null && bOrder != null) return 1;
        if (aOrder != null && bOrder == null) return -1;
        if (aOrder != null && bOrder != null && aOrder !== bOrder) return aOrder - bOrder;
        if (a.data_element_name && b.data_element_name) return a.data_element_name.localeCompare(b.data_element_name);
        return 0;
      });
      groupedBySection[key] = sortedItems;
      sectionOrder.push(key);
    });
  });
  sectionOrder.forEach(section => {
    sectionDataSources[section] = new MatTableDataSource(groupedBySection[section]);
  });
  const miscIndex = sectionOrder.findIndex(s => s === 'Miscellaneous');
  if (miscIndex !== -1) {
    sectionOrder.splice(miscIndex, 1);
    sectionOrder.push('Miscellaneous');
    sectionDataSources['Miscellaneous'] = new MatTableDataSource(groupedBySection['Miscellaneous']);
  }
  return { groupedBySection, sectionOrder, sectionDataSources };
}

export function shouldShowSectionIcon(section: string, sectionDataSources: { [section: string]: MatTableDataSource<any> }): boolean {
  const data = sectionDataSources[section]?.data;
  const isBorrowerBackground = (str: string) =>
    typeof str === 'string' && str.trim().toLowerCase() === 'borrower background';
  if (isBorrowerBackground(section)) {
    if (Array.isArray(data) && data.length > 0) {
      if (data.every((el: any) => el.level === 2)) {
        return false;
      }
    }
  }
  if (Array.isArray(data) && data.length > 0) {
    if (data.some((el: any) => isBorrowerBackground(el.data_element_name) && el.level === 2)) {
      return false;
    }
    if (data.some((el: any) => el.level === 2)) {
      return false;
    }
  }
  if (!section || section.trim() === '') {
    if (Array.isArray(data) && data.length > 0) {
      return !data.every((el: any) => el.level === 0 || el.level === 1 || el.level === 2);
    }
    return false;
  }
  if (section === 'Miscellaneous' || section === 'None') {
    return false;
  }
  return Array.isArray(data) ? data.some((el: any) => el.level !== 2) : true;
}

export function collapseAllDependents(sectionDataSources: { [section: string]: MatTableDataSource<any> }) {
  Object.entries(sectionDataSources).forEach(([section, ds]: [string, any]) => {
    if (ds && Array.isArray(ds.data)) {
      const parentRows = ds.data.filter((row: any) => !row.isDependent);
      parentRows.forEach((row: any) => {
        if (row && row.dependentExpanded) {
          row.dependentExpanded = false;
        }
      });
      ds.data = ds.data.filter((row: any) => !row.isDependent);
    }
  });
}
